import { motion, useAnimation } from "framer-motion";
import { useEffect, useRef } from "react";
import { Heart } from "lucide-react";

interface Props {
  isRsvped: boolean;
  isInterested: boolean;
  showRsvp: boolean;
  isFull: boolean;
  interestedPending: boolean;
  onRsvp: () => void;
  onInterested: () => void;
  variant?: "floating" | "inline";
}

export function EventActionBar({
  isRsvped: _isRsvped,
  isInterested,
  showRsvp: _showRsvp,
  isFull: _isFull,
  interestedPending,
  onRsvp: _onRsvp,
  onInterested,
  variant = "floating",
}: Props) {
  const pill = (
    <motion.div
      initial={variant === "floating" ? { opacity: 0, y: 16, scale: 0.92 } : false}
      animate={variant === "floating" ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ type: "spring", stiffness: 340, damping: 28, delay: 0.15 }}
      className="inline-flex items-center rounded-full backdrop-blur bg-neutral-900/80 border border-white/[0.08] shadow-xl shadow-black/30"
    >
      <HeartButton
        isInterested={isInterested}
        disabled={interestedPending}
        onClick={onInterested}
      />
    </motion.div>
  );

  if (variant === "inline") return pill;

  return (
    <div className="fixed left-0 right-0 z-40 above-nav-sticky flex justify-center px-4 sm:px-6 pb-4 pointer-events-none">
      <div className="pointer-events-auto">{pill}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------


function HeartButton({
  isInterested,
  disabled,
  onClick,
}: {
  isInterested: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const controls = useAnimation();
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (isInterested) {
      controls.start({
        scale: [0.2, 1.75, 0.82, 1.5, 0.88, 1.08, 1],
        transition: { duration: 0.6, times: [0, 0.2, 0.38, 0.55, 0.72, 0.88, 1] },
      });
    } else {
      controls.start({
        scale: [1, 1.2, 0.4, 1],
        transition: { duration: 0.3, times: [0, 0.2, 0.55, 1] },
      });
    }
  }, [isInterested, controls]);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={isInterested}
      className={`
        inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
        btn-primary
        transition-all duration-150 focus:outline-none active:scale-95
        ${isInterested ? "text-rose-400 dark:text-rose-400 hover:text-rose-600 dark:hover:text-rose-600 active:text-rose-700" : ""}
      `}
    >
      <motion.span animate={controls} className="shrink-0">
        <Heart className={`w-4 h-4 ${isInterested ? "fill-current" : ""}`} />
      </motion.span>
      Interested
    </button>
  );
}

